import { useCallback, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { useExportAnalysisSummary } from "@/api/analysis/useAnalysisAPI";
import { CloseIcon, DownloadIcon, MaximizeIcon } from "@/assets/icons";
import { AnalysisSummaryDataProvider } from "@/contexts/AnalysisSummaryDataContext";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useExportAnalysisSummaryStatus } from "@/hooks/spatialAnalysis/useExportAnalysisSummaryStatus";
import { RootState } from "@/store";
import {
  setAnalysisSummaryId,
  setGeneralSummary
} from "@/store/slice/analysisSummary";
import {
  setAnalysisResultPopup,
  setShowAnalysisSummaryPopup
} from "@/store/slice/uiSlice";
import { ExportDatasetFileType } from "@/types/dataset";

import dayjs from "dayjs";
import html2canvas from "html2canvas";
import { twMerge } from "tailwind-merge";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { AnalysisSummaryWrapper } from "./analysisSummaryWrapper";
import PrintAnalysisSummary from "./printSummary";

export default function SummaryAnalysis() {
  const dispatch = useDispatch();
  const { restoreAllLayersVisibility, handleClearHighlightedGrids } = useMap();
  const { map } = useMap();

  const printRef = useRef<HTMLDivElement | null>(null);
  const dynamicChartRef = useRef<(HTMLDivElement | null)[]>([]);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const promiseResolveRef = useRef<
    ((value: void | PromiseLike<void>) => void) | null
  >(null);

  const { analysisSummaryId, spatialAnalysisDatasetId } = useSelector(
    (state: RootState) => state.analysisSummary
  );
  const { title: spatialTitle } = useSelector(
    (state: RootState) => state.spatial
  );
  const [chartImageData, setChartImageData] = useState<string | null>(null);
  const [dynamicChartImageData, setDynamicChartImageData] = useState<string[]>(
    []
  );
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [mapImage, setMapImage] = useState<string | null>(null);

  const { setOperationId } = useExportAnalysisSummaryStatus();
  const { mutate: exportAnalysisSummary } = useExportAnalysisSummary({
    onSuccess: (data) => {
      setOperationId(data.operation_id);
    }
  });

  const reactToPrintFn = useReactToPrint({
    documentTitle: `SummaryAnalysis${
      spatialTitle ? `_${spatialTitle}` : ""
    }_${dayjs().format("YYYYMMDDHHmm")}`,
    onBeforePrint: async () => {
      const chart = await getChartPng();
      const dynamicChartImages = await generateAllChartImages();

      if (chart) setChartImageData(chart);
      if (dynamicChartImages)
        setDynamicChartImageData(dynamicChartImages as string[]);

      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    }
  });

  const getChartPng = useCallback(async () => {
    if (chartRef.current) {
      const canvas = await html2canvas(chartRef.current, {
        ignoreElements: (element) => {
          return element.classList?.contains("print:hidden") || false;
        }
      });
      return canvas.toDataURL("image/png", 1);
    }
    return null;
  }, []);

  const generateAllChartImages = useCallback(async () => {
    if (!dynamicChartRef.current) {
      return [];
    }

    const imagePromises = dynamicChartRef.current.map(async (chartElement) => {
      if (chartElement) {
        const canvas = await html2canvas(chartElement, {
          ignoreElements: (element) => {
            return element.classList?.contains("print:hidden") || false;
          }
        });
        return canvas.toDataURL("image/png", 1);
      }
      return null;
    });

    const base64Images = await Promise.all(imagePromises);
    return base64Images.filter(Boolean);
  }, []);

  const handleClose = () => {
    dispatch(setAnalysisSummaryId(null));
    dispatch(setGeneralSummary(null));
    dispatch(setShowAnalysisSummaryPopup(false));
    dispatch(setAnalysisResultPopup(true));

    handleClearHighlightedGrids(spatialAnalysisDatasetId);
    restoreAllLayersVisibility();
  };

  const handlePrint = () => {
    if (map) {
      const canvas = map.getCanvas();
      const dataURL = canvas.toDataURL("image/png");
      setMapImage(dataURL);

      reactToPrintFn(() => printRef.current);
    }
  };

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      handleClose();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <Dialog open onOpenChange={handleClose} modal={false}>
      {isExpanded && (
        <div
          className="fixed inset-0 bg-black/60 pointer-events-none z-[100]"
          data-testid="summary-overlay"
        />
      )}
      <DialogTitle className="sr-only">Analysis Summary</DialogTitle>
      <DialogDescription className="sr-only">
        Analysis Summary
      </DialogDescription>
      <DialogContent
        className={twMerge(
          "flex h-[calc(100%-24px)] gap-0 p-0 bg-white overflow-auto",
          isExpanded
            ? "max-w-[calc(100%-24px)]"
            : "w-[514px] !translate-x-0 !left-auto !right-24"
        )}
        isHideClose
        onInteractOutside={(e) => !isExpanded && e.preventDefault()}
      >
        <AnalysisSummaryDataProvider>
          <div className="flex w-full h-full">
            <div className="relative flex flex-col w-full h-full">
              <div className="summary-header flex flex-row items-center justify-between pt-4 px-4 sticky top-0 z-10 ">
                <h1 className="text-xl font-semibold text-txtHeading">
                  Analysis Summary
                </h1>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    className="size-auto [&_svg]:size-6"
                    size="icon"
                    aria-label="Maximize"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    <MaximizeIcon className="fill-dim-gray" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-auto [&_svg]:size-6"
                        aria-label="Export"
                      >
                        <DownloadIcon className="fill-dim-gray" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="z-[99999]">
                      <DropdownMenuItem onClick={handlePrint}>
                        PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          exportAnalysisSummary({
                            analysisId: analysisSummaryId || "",
                            fileType: ExportDatasetFileType.XLSX
                          })
                        }
                      >
                        XLSX
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          exportAnalysisSummary({
                            analysisId: analysisSummaryId || "",
                            fileType: ExportDatasetFileType.CSV
                          })
                        }
                      >
                        CSV
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-auto [&_svg]:size-6"
                    aria-label="Close summary panel"
                    onClick={handleClose}
                  >
                    <CloseIcon className="fill-dim-gray" />
                  </Button>
                </div>
              </div>
              <AnalysisSummaryWrapper
                chartRef={chartRef}
                dynamicChartRef={dynamicChartRef}
                isExpanded={isExpanded}
              />
            </div>
          </div>

          {createPortal(
            <div className="absolute left-[0] top-[0] bg-white w-full h-full hidden">
              <PrintAnalysisSummary
                ref={printRef}
                mapImage={mapImage}
                chartImageData={chartImageData}
                dynamicChartImageData={dynamicChartImageData}
              />
            </div>,
            document.body
          )}
        </AnalysisSummaryDataProvider>
      </DialogContent>
    </Dialog>
  );
}
